import type { AccessibilitySecurity } from './security';

// ─── Types ────────────────────────────────────────────────────────────────────

export type S3SecurityType = 'ETF_EQUITY' | 'ETF_BOND' | 'MF_EQUITY' | 'MF_BOND' | 'STARS_ENRT' | 'STARS_ENRN';

export type ChartTypeCode =
    | 'econ_sector'
    | 'perf10k'
    | 'shout_tna'
    | 'credit_quality'
    | 'asset_allocation'
    | 'summary_credit_rating'
    | 'price_performance'
    | 'relative_performance';

export type ChartTypeOption = {
    code: ChartTypeCode;
    label: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const SECURITY_TYPE_S3_PATH: Record<S3SecurityType, string> = {
    ETF_EQUITY: 'ETF/Equity',
    ETF_BOND: 'ETF/Bonds',
    MF_EQUITY: 'MF/Equity',
    MF_BOND: 'MF/Bonds',
    STARS_ENRT: 'STARS/ENRT',
    STARS_ENRN: 'STARS/ENRN',
};

export const VALID_CHART_TYPES: Record<S3SecurityType, ChartTypeCode[]> = {
    ETF_EQUITY: ['econ_sector', 'perf10k', 'shout_tna'],
    ETF_BOND: ['credit_quality', 'perf10k', 'shout_tna'],
    MF_EQUITY: ['econ_sector', 'perf10k'],
    MF_BOND: ['credit_quality', 'perf10k', 'asset_allocation', 'summary_credit_rating'],
    STARS_ENRT: ['price_performance'],
    STARS_ENRN: ['relative_performance'],
};

export const CHART_TYPE_DISPLAY_NAMES: Record<ChartTypeCode, string> = {
    econ_sector: 'Economic Sector Representation',
    perf10k: 'Performance of $10k Investment',
    shout_tna: 'Shares Outstanding and Total Net Assets',
    credit_quality: 'Credit Quality Breakdown',
    asset_allocation: 'Asset Allocation',
    summary_credit_rating: 'Summary Credit Rating',
    price_performance: 'Price Performance',
    relative_performance: 'Relative Performance',
};

export const CHART_DATA_PATH = 'chart-data';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolves the S3 security type from the selected security and (for Stock)
 * the selected chart type.
 *
 * Mapping rules:
 *   ETF  + holding_asset_class = "Equity"         → ETF_EQUITY
 *   ETF  + holding_asset_class = "Fixed Income"   → ETF_BOND
 *   Fund + category_name       = "Equity"         → MF_EQUITY
 *   Fund + category_name contains "Fixed Income"  → MF_BOND
 *   Stock + chartTypeCode = price_performance     → STARS_ENRT
 *   Stock + chartTypeCode = relative_performance  → STARS_ENRN
 *
 * Returns null when the sub-type cannot be determined from available fields.
 */
export function resolveS3SecurityType(
    security: AccessibilitySecurity,
    chartTypeCode?: ChartTypeCode,
): S3SecurityType | null {
    const secType = security.security_type.toLowerCase();

    if (secType === 'etf') {
        const assetClass = security.holding_asset_class?.toLowerCase();
        if (assetClass === 'equity') return 'ETF_EQUITY';
        if (assetClass === 'fixed income') return 'ETF_BOND';
        return null;
    }

    if (secType === 'fund') {
        const category = security.category_name?.toLowerCase();
        if (category === 'equity') return 'MF_EQUITY';
        if (category?.includes('fixed income') || category?.includes('bond')) return 'MF_BOND';
        return null;
    }

    if (secType === 'stock') {
        if (chartTypeCode === 'price_performance') return 'STARS_ENRT';
        if (chartTypeCode === 'relative_performance') return 'STARS_ENRN';
        return null;
    }

    return null;
}

/**
 * Resolves the identifier sent to chart-data as `security_id`, chosen by
 * security type and always returned as a string ('' when unavailable):
 *   Stock → tradingitem_id
 *   ETF   → etf_fund_no
 *   Fund  → mf_fund_no
 */
export function resolveSecurityId(security: AccessibilitySecurity): string {
    const secType = security.security_type.toLowerCase();

    let id: number | string | undefined;
    if (secType === 'stock') id = security.tradingitem_id;
    else if (secType === 'etf') id = security.etf_fund_no;
    else if (secType === 'fund') id = security.mf_fund_no;

    return id != null ? String(id) : '';
}

/**
 * Returns the available chart type codes for a given security.
 * For Stock, combines STARS_ENRT and STARS_ENRN options — the sub-type is
 * resolved later once the user picks a chart type.
 */
export function getChartTypesForSecurity(security: AccessibilitySecurity): ChartTypeCode[] {
    const secType = security.security_type.toLowerCase();

    if (secType === 'stock') {
        return [...VALID_CHART_TYPES.STARS_ENRT, ...VALID_CHART_TYPES.STARS_ENRN];
    }

    const s3Type = resolveS3SecurityType(security);
    if (!s3Type) return [];
    return VALID_CHART_TYPES[s3Type];
}

